/**
 * Sheet „Person anlegen" (`access-invite-sheet`, Spec 25, Oberfläche C —
 * ersetzt „Person einladen" aus Spec 05a: E1 schließt jede Mail aus, der
 * Administrator gibt Name, E-Mail **und Passwort** ein und sagt der Person
 * das Passwort persönlich). Zwei Einstiege teilen sich dieses Sheet: das
 * Konto-Menü (`account-access` → `screen-access` → `access-invite`, ohne
 * Objekt) und `access-add-invite` im Zugriff-Sheet eines Objekts
 * (`presetEntity` gesetzt, „Weitere Angaben" gleich aufgeklappt, das
 * Objektfeld gesperrt — dieselbe Form wie ein vorbelegtes Elternobjekt beim
 * Duplizieren, Spec 02b). Bezeichner bleiben `access-invite-*` (R6, rund 60
 * Stellen kennen sie); nur die Beschriftungen wechseln.
 *
 * Sichtbar sind E-Mail, Name, Passwort, Art und der Schalter Vollzugriff
 * (aus für externe Personen, Fachregel 2/DB-Check); Objekt, Rolle und
 * Ablaufdatum stecken weiter hinter „Weitere Angaben" (dasselbe Muster wie
 * `PropertySheet`). `POST /access/users` erzeugt Person **und** ersten
 * Zugriff in einer Transaktion, wenn ein Objekt gewählt ist — `grant` bleibt
 * sonst weg.
 *
 * Keine optimistische Anzeige: Wie `TenantCreateSheet` (Spec 03) wartet diese
 * Person auf die echte Antwort, bevor sie in der Liste steht — anders als
 * `PropertySheet`/`BuildingSheet`/`UnitSheet`, deren optimistische Baumzeile
 * eine andere Ansicht bedient (den Objektbaum), die es hier nicht gibt.
 */

import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { berlinEndOfDayIso } from '@/lib/date'
import { useCreateUser, usePersonKinds } from '@/features/access/api'
import type { PersonCreate } from '@/features/access/api'
import { FullAccessCheckbox } from '@/features/access/FullAccessCheckbox'
import { PasswordRevealField } from '@/features/access/PasswordRevealField'
import { RoleSelectField } from '@/features/access/RoleSelectField'
import { EntityPicker } from '@/components/EntityPicker'
import type { EntitySelection } from '@/components/EntityPicker'
import { EnumSelectField } from '@/components/EnumSelectField'
import type { EnumOption } from '@/components/EnumSelectField'
import { FIELD_CLASS, FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

interface AccessInviteSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /** Gesetzt für `access-add-invite` (Spec: „mit dem Objekt bereits
   * gesetzt") — das Objektfeld ist dann vorbelegt und gesperrt. */
  readonly presetEntity?: EntitySelection
}

/** `permissions/core.py::GrantRole.viewer` — „Lesen" ist die Vorbelegung
 * (Spec: „vorbelegt 'Lesen'"). Kein Rangvergleich, nur der bekannte, stabile
 * Wert des niedrigsten Enum-Mitglieds als Startzustand des Formulars —
 * dieselbe Art Vorbelegung wie `DEFAULT_LEGAL_FORM` in `TenantCreateSheet`. */
const DEFAULT_ROLE = 'viewer'

const EMPTY_FORM = {
  email: '',
  displayName: '',
  password: '',
  // `'true'`/`'false'` bleibt die Formularkodierung aus 05a — `<select>` kennt
  // nur Strings, und der Rumpf will am Ende ein `boolean` (`is_external`,
  // ADR 0008). Angefasst wird sie hier nicht: Die 05a-Journey wählt
  // `selectOption('true')` und prüft `toHaveValue('true')`, und AK-380
  // verbietet, eine der acht älteren Journey-Dateien anzufassen. Was sich mit
  // Spec 05b ändert, ist nur die Herkunft der **Beschriftung** — die kommt
  // jetzt von `GET /access/person-kinds` statt hier fest zu stehen (AK-382).
  isExternal: 'false',
  fullAccess: false,
  entity: null as EntitySelection | null,
  role: DEFAULT_ROLE,
  expiresAt: '',
}

type FormValues = typeof EMPTY_FORM

function toBody(form: FormValues): PersonCreate {
  return {
    email: form.email,
    display_name: form.displayName,
    password: form.password,
    is_external: form.isExternal === 'true',
    // Extern und Vollzugriff schließen sich aus (Fachregel 2, DB-Check
    // `app_users_external_no_full_access_ck`) — der Schalter ist für Extern
    // schon deaktiviert und aus (`FullAccessCheckbox`-Aufruf unten), diese
    // Zeile hält dieselbe Regel auch dann ein, wenn „Art" nach dem Anschalten
    // von Vollzugriff noch auf „Extern" gewechselt wird.
    full_access: form.isExternal === 'true' ? false : form.fullAccess,
    ...(form.entity !== null
      ? {
          grant: {
            entity_id: form.entity.id,
            role: form.role,
            ...(form.expiresAt === '' ? {} : { expires_at: berlinEndOfDayIso(form.expiresAt) }),
          },
        }
      : {}),
  }
}

export function AccessInviteSheet({ open, onOpenChange, presetEntity }: AccessInviteSheetProps) {
  const createMutation = useCreateUser()
  const personKindsQuery = usePersonKinds()
  const emailRef = useRef<HTMLInputElement>(null)

  // `value === 'external'` ist dieselbe Zuordnung, die die Spec für diesen
  // Übergang vorsieht (Spec 05b, „Die Anzeige allein reicht nicht"): Der
  // DOM-Wert bleibt `'true'`/`'false'` (siehe `EMPTY_FORM` oben), nur die
  // Beschriftung kommt jetzt vom Server statt hier als deutsches Wort zu
  // stehen.
  const personKindOptions: EnumOption[] = (personKindsQuery.data ?? []).map((kind) => ({
    value: kind.value === 'external' ? 'true' : 'false',
    label: kind.label,
  }))

  function initialForm(): FormValues {
    return {
      ...EMPTY_FORM,
      entity: presetEntity ?? null,
    }
  }

  const [form, setForm] = useState<FormValues>(initialForm)
  const [isMoreOpen, setIsMoreOpen] = useState(presetEntity !== undefined)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen beginnt frisch (React-Muster „Adjusting state when a prop
  // changes", während des Renderns statt in einem Effekt) — dieselbe Technik
  // wie in `PropertySheet`/`TenantCreateSheet`.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm(initialForm())
      setIsMoreOpen(presetEntity !== undefined)
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  function updateField<K extends keyof FormValues>(name: K) {
    return (value: FormValues[K]) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  function resetForAnother() {
    // „Felder E-Mail/Name/Passwort leer, Art/Objekt/Vollzugriff bleiben"
    // (Spec 25, Endpunkte „Aktionen") — Rolle und Ablauf gehören zum
    // Objektbezug, nicht zur Person, und werden mit zurückgesetzt, wenn kein
    // Objekt (mehr) gewählt ist.
    setForm((current) => ({
      ...EMPTY_FORM,
      isExternal: current.isExternal,
      fullAccess: current.fullAccess,
      entity: current.entity,
      role: current.role,
    }))
    setFieldError(undefined)
    setGeneralError(undefined)
    // „Fokus im ersten Feld" (`design-system`, „Und noch eins").
    emailRef.current?.focus()
  }

  async function submit(andAnother: boolean) {
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      await createMutation.mutateAsync(toBody(form))
      if (andAnother) {
        resetForAnother()
      } else {
        onOpenChange(false)
      }
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          emailRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form
          data-testid="access-invite-sheet"
          onSubmit={(event) => void handleSubmit(event)}
          className="flex h-full flex-col"
        >
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              Person anlegen
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <FormField
              ref={emailRef}
              label="E-Mail"
              testId="access-invite-email"
              apiField="email"
              errorPrefix="access-invite"
              type="email"
              inputMode="email"
              required
              value={form.email}
              onChange={updateField('email')}
              error={fieldError}
            />

            <FormField
              label="Name"
              testId="access-invite-name"
              apiField="display_name"
              errorPrefix="access-invite"
              required
              value={form.displayName}
              onChange={updateField('displayName')}
              error={fieldError}
            />

            <div className="flex flex-col gap-1">
              <PasswordRevealField
                label="Passwort"
                testId="access-invite-password"
                apiField="password"
                errorPrefix="access-invite"
                required
                value={form.password}
                onChange={updateField('password')}
                error={fieldError}
              />
              {/* Fachregel 1, Oberfläche C: kein Hinweis „Mail verschickt" —
               * es gibt keine (E1). */}
              <p data-testid="access-invite-password-hint" className="text-label text-muted">
                Du gibst das Passwort persönlich weiter. Ändern kann es die Person später selbst.
              </p>
            </div>

            <EnumSelectField
              label="Art"
              testId="access-invite-external"
              apiField="is_external"
              errorPrefix="access-invite"
              options={personKindOptions}
              isPending={personKindsQuery.isPending}
              value={form.isExternal}
              onChange={(value) => {
                updateField('isExternal')(value)
                // Extern → Vollzugriff sofort aus (Fachregel 2) — nicht nur
                // deaktiviert, sonst sendete ein unveränderter Schalterstand
                // beim Zurückwechseln auf „Intern" einen falschen Rumpfwert.
                if (value === 'true') updateField('fullAccess')(false)
              }}
              error={fieldError}
            />

            <FullAccessCheckbox
              testId="access-invite-full-access"
              checked={form.fullAccess}
              onChange={updateField('fullAccess')}
              disabled={form.isExternal === 'true'}
            />

            <details
              open={isMoreOpen}
              onToggle={(event) => {
                setIsMoreOpen(event.currentTarget.open)
              }}
            >
              <summary
                data-testid="access-invite-more"
                className="text-label text-muted min-h-(--tap-min) cursor-pointer list-item py-2"
              >
                Weitere Angaben
              </summary>

              <div className="flex flex-col gap-4 pt-2">
                <EntityPicker
                  testId="access-invite-entity"
                  label="Zugriff auf"
                  value={form.entity}
                  onSelect={updateField('entity')}
                  error={fieldError}
                  errorPrefix="access-invite"
                  disabled={presetEntity !== undefined}
                />

                <RoleSelectField
                  testId="access-invite-role"
                  errorPrefix="access-invite"
                  value={form.role}
                  onChange={updateField('role')}
                  error={fieldError}
                />

                {/* „Letzter Tag mit Zugriff", nicht „Zugriff endet am" —
                 * `docs/FACHREGELN.md` Regel 9: Ein Irrtum bei der Auslegung
                 * läuft mit dieser Formulierung harmlos (ein Tag zu viel
                 * statt jemand ausgesperrt), Vorschlag des `design-reviewer`
                 * (K3/W2). */}
                <FormField
                  label="Letzter Tag mit Zugriff"
                  testId="access-invite-expires"
                  apiField="expires_at"
                  errorPrefix="access-invite"
                  type="date"
                  value={form.expiresAt}
                  onChange={updateField('expiresAt')}
                  error={fieldError}
                />
              </div>
            </details>
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="access-invite-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="access-invite-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="button"
                data-testid="access-invite-save-another"
                disabled={createMutation.isPending}
                aria-busy={createMutation.isPending}
                onClick={() => void submit(true)}
                className={`text-text px-4 ${FIELD_CLASS} disabled:opacity-60`}
              >
                Anlegen und noch eine
              </button>
              <button
                type="submit"
                data-testid="access-invite-save"
                disabled={createMutation.isPending}
                aria-busy={createMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Anlegen
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
